import {PriorityConfig} from '@aboutyou/data-fetcher';
import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  Grid,
  ProductImageType,
  useScrollToTopOnUnload,
} from '@aboutyou/ui-components';
import React, {useEffect} from 'react';
import styled from 'styled-components';
import {useActivatePreselectedBestCouponABTest} from '../../../features/Coupons/useActivatePreselectedBestCouponABTest';
import {ProductStreamEmpty} from '../../../features/ProductStream/ProductStreamEmpty';
import {ProductStreamEmptyDeals} from '../../../features/ProductStream/ProductStreamEmptyDeals';
import {
  StreamTileList,
  StreamTileListProps,
} from '../../../features/ProductStream/StreamTileList';
import {TilesByPage} from '../../../features/ProductStream/TilesByPage';
import {
  StreamPaddingCSS,
  labelToProductImageType,
} from '../../../features/ProductStream/helpers';
import {usePotentialFilters} from '../../../features/ProductStream/utils';
import {useLoadMoreButton} from '../../../features/ProductStream/utils/useLoadMoreButton';
import {
  LoadMoreComponentDisplayConfig,
  ProductStreamTokenPagination,
} from '../../../features/ProductStreamPagination/ProductStreamTokenPagination';
import {ProductTileProps} from '../../../features/ProductTile/ProductTile';
import {useShallowRenderingState} from '../../../features/ProductTile/utils';
import {useIsInitialStreamRequestEmpty} from '../../../features/TokenBasedStreamPages/useIsInitialStreamRequestEmpty';
import {useTokenBasedStreamPages} from '../../../features/TokenBasedStreamPages/useTokenBasedStreamPages';
import {useIsCampaignStickyBarOpen} from '../../Campaigns/hooks/useIsCampaignStickyBarOpen';
import {usePreventProductTilesSwipeAnimation} from '../../CouponLandingPage/ProductStream/usePreventProductTilesSwipeAnimation';
import {useActivatePPIAbTest} from '../utils/useActivatePPIAbTest';
import {useCategoryProductStream} from '../utils/useCategoryProductStream';
import {useCategoryProductStreamNext} from '../utils/useCategoryProductStreamNext';
import {useIsDiscountSelected} from '../utils/useIsDiscountSelected';
import {AdditionalProductStream} from './AdditionalProductStream';

const HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY =
  'category_product_stream_last_visible_items';

export type ProductStreamProps = {
  preferredProductImageType?: ProductImageType;
  loadMoreComponentDisplayConfig: LoadMoreComponentDisplayConfig;
  totalPages?: number;
  enableImageGalleryAnimation?: boolean;
  ProductTileComponent?: React.FC<ProductTileProps>;
  enableOutfitSlider?: boolean;
} & StreamTileListProps &
  PriorityConfig;

export const ProductStream: React.FC<ProductStreamProps> = ({
  constrainStoryTilesToViewportBoundary,
  gridColumns,
  preferredProductImageType,
  inlineBASPlacements,
  loadMoreComponentDisplayConfig,
  renderingPriority,
  onClickAddCta,
  addCtaVisibility,
  addCtaTitle,
  showLowStockHint = false,
  onTileVisible,
  layoutConfig,
  productTileGalleryMode,
  interactiveHoverMode,
  enableImageGalleryAnimation,
  ProductTileComponent,
  enableOutfitSlider,
}) => {
  const {pages, isNextPageReady, mountNextPage, endOfStreamReached} =
    useTokenBasedStreamPages({
      uniqueKey: 'category',
      request: useCategoryProductStream,
      nextRequest: useCategoryProductStreamNext,
    });

  const shallowRenderingState = useShallowRenderingState({
    uniqueKey: HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY,
  });

  const preventProductTilesSwipeAnimation =
    usePreventProductTilesSwipeAnimation();

  const isInitialRequestEmpty = useIsInitialStreamRequestEmpty(
    useCategoryProductStream,
  );

  useScrollToTopOnUnload();

  useActivatePreselectedBestCouponABTest();

  useActivatePPIAbTest();

  return (
    <section>
      <StyledGrid
        columns={gridColumns}
        enableEnhancedTileRendering
        uniqueKey="productStream"
      >
        {pages.map((page, i) => (
          <TilesByPage
            key={i}
            page={page}
            StreamTileList={StreamTileList}
            renderingPriority={renderingPriority}
            gridColumns={gridColumns}
            preferredProductImageType={labelToProductImageType(
              preferredProductImageType,
            )}
            inlineBASPlacements={inlineBASPlacements}
            constrainStoryTilesToViewportBoundary={
              constrainStoryTilesToViewportBoundary
            }
            shallowRenderingState={shallowRenderingState}
            onClickAddCta={onClickAddCta}
            addCtaVisibility={addCtaVisibility}
            addCtaTitle={addCtaTitle}
            showLowStockHint={showLowStockHint}
            onTileVisible={onTileVisible}
            layoutConfig={layoutConfig}
            hideTopUSP={isInitialRequestEmpty}
            productTileGalleryMode={productTileGalleryMode}
            interactiveHoverMode={interactiveHoverMode}
            enableImageGalleryAnimation={
              enableImageGalleryAnimation && !preventProductTilesSwipeAnimation
            }
            ProductTileComponent={ProductTileComponent}
            enableOutfitSlider={enableOutfitSlider}
          />
        ))}
      </StyledGrid>
      {isNextPageReady && (
        <ProductStreamPagination
          canShowLoadMoreButton={pages.length === 1}
          mountNextPage={mountNextPage}
          loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
        />
      )}
      {isInitialRequestEmpty && <EmptyStream />}
      {endOfStreamReached && (
        <AdditionalProductStream
          renderingPriority={renderingPriority}
          gridColumns={gridColumns}
          preferredProductImageType={preferredProductImageType}
          inlineBASPlacements={inlineBASPlacements}
          loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
          constrainStoryTilesToViewportBoundary={
            constrainStoryTilesToViewportBoundary
          }
          onClickAddCta={onClickAddCta}
          showLowStockHint={showLowStockHint}
          ProductTileComponent={ProductTileComponent}
          layoutConfig={layoutConfig}
          enableOutfitSlider={enableOutfitSlider}
        />
      )}
    </section>
  );
};

const ProductStreamPagination: React.FC<{
  canShowLoadMoreButton: boolean;
  mountNextPage: VoidFunction;
  loadMoreComponentDisplayConfig: LoadMoreComponentDisplayConfig;
}> = ({
  canShowLoadMoreButton,
  mountNextPage,
  loadMoreComponentDisplayConfig,
}) => {
  const {t} = useTranslation();
  const {shouldShowLoadMoreButton: showButton, onClick} = useLoadMoreButton();
  const shouldShowLoadMoreButton = canShowLoadMoreButton && showButton;

  const onLoadMoreButtonClick = () => {
    mountNextPage();
    onClick();
  };

  return shouldShowLoadMoreButton ? (
    <LoadMoreButton
      data-testid="loadMoreButton"
      variant="secondary"
      onClick={onLoadMoreButtonClick}
    >
      {t('Load more products')}
    </LoadMoreButton>
  ) : (
    <ProductStreamTokenPagination
      mountNextPage={mountNextPage}
      loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
    />
  );
};

const EmptyStream = () => {
  const {resetAllFilters} = usePotentialFilters();
  const isDiscountSelected = useIsDiscountSelected();

  const {isOpen: isCampaignStickyBarOpen} = useIsCampaignStickyBarOpen();

  // On category page, when switching to a discount that yields no results, the empty stream state is shown
  // and since the scroll position is kept, the user sees the footer.
  // For a nicer experience, in that case we should scroll to the top. https://aboutyou.atlassian.net/browse/ASFE-8955
  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setTimeout(() => {
      window.scrollTo({top: 0, behavior: 'smooth'});
    });
  }, [isCampaignStickyBarOpen]);

  if (isDiscountSelected) {
    return <ProductStreamEmptyDeals />;
  }
  return <ProductStreamEmpty onFilterClear={resetAllFilters} />;
};

const StyledGrid = styled(Grid)`
  ${StreamPaddingCSS}
`;

const LoadMoreButton = styled(Button)`
  &&& {
    margin: 40px auto 80px auto;
  }
`;
